/**
 * useVendorAuth — Authentication hook for Vendor Portal
 * Resolves vendor identity and enforces vendor role.
 */
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { resolveVendorProfile, type VendorProfile } from '../services/vendorService';

interface VendorAuthState {
  profile: VendorProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
}

export function useVendorAuth(): VendorAuthState {
  const [profile, setProfile] = useState<VendorProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const resolve = async (userId: string) => {
      try {
        const p = await resolveVendorProfile(userId);
        if (mounted) setProfile(p);
      } catch {
        if (mounted) setProfile(null);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      if (!mounted) return;
      if (session?.user) {
        resolve(session.user.id);
      } else {
        setProfile(null);
      }
    });

    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!mounted) return;
        if (session?.user) {
          await resolve(session.user.id);
        }
      } catch {
        // ignore
      } finally {
        if (mounted) setLoading(false);
      }
    };
    init();

    return () => { mounted = false; subscription.unsubscribe(); };
  }, []);

  return {
    profile,
    loading,
    isAuthenticated: !!profile,
  };
}
